// src/pages/admin/AdminPendingUsers.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import { useAuth } from "@/context/AuthProvider";
import { toast } from "react-hot-toast";

const ROLES = ["viewer", "manager", "procurement", "finance", "admin", "developer"];

export default function AdminPendingUsers() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [role, setRole] = useState("viewer");
  const [selectedCC, setSelectedCC] = useState(new Set());

  // ✅ Units (only needed when UNIT_REQUIRED)
  const [units, setUnits] = useState([]); // [{id, unit_name, slug}]
  const [unitId, setUnitId] = useState(""); // string for <select>
  const [unitRequired, setUnitRequired] = useState(false);

  const canAdmin = useMemo(() => {
    return ["admin", "finance", "developer"].includes(user?.role);
  }, [user]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, ccRes] = await Promise.all([
        api.get("/solo-mdsh-api/admin/users/pending"),
        api.get("/solo-mdsh-api/cost-centres"),
      ]);
      setPending(pRes.data || []);
      setCentres((ccRes.data || []).filter((c) => c.is_active !== false));
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openApprove = (u) => {
    setSelectedUser(u);
    setRole("viewer");
    setSelectedCC(new Set());

    // reset unit picker state
    setUnits([]);
    setUnitId("");
    setUnitRequired(false);
    setError(null);
  };

  const toggleCC = (id) => {
    setSelectedCC((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  async function sendApprove({ pickedUnitId }) {
    if (!selectedUser) return;

    const payload = {
      role,
      cost_centre_ids: Array.from(selectedCC),
      ...(pickedUnitId ? { unit_id: Number(pickedUnitId) } : {}),
    };

    return api.patch(`/solo-mdsh-api/admin/users/${selectedUser.id}/approve`, payload);
  }

  const approve = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setError(null);

    try {
      // If we already know unit is required, block until selection
      if (unitRequired && !unitId) {
        const msg = "Please select a unit to approve this user.";
        setError(msg);
        toast.error(msg);
        return;
      }

      await sendApprove({ pickedUnitId: unitId });

      toast.success(`✅ Approved ${selectedUser.full_name} as ${role}`);
      setSelectedUser(null);
      await fetchAll();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      const code = data?.error || "";

      // ✅ Backend enforcement: requires unit selection when multiple units
      if (status === 400 && code === "UNIT_REQUIRED") {
        const list = Array.isArray(data?.units) ? data.units : [];
        setUnits(list);
        setUnitRequired(true);

        // preselect first unit for convenience (still visible to admin)
        if (!unitId && list.length > 0) setUnitId(String(list[0].id));

        const msg = data?.message || "Multiple units found. Please choose a unit.";
        setError(msg);
        toast(msg);
        return;
      }

      const msg = data?.error || data?.message || "Failed to approve user";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const reject = async (id, full_name) => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/solo-mdsh-api/admin/users/${id}/reject`);
      toast.success(`❌ Rejected ${full_name}`);
      await fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.error || "Failed to reject user";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!canAdmin) {
    return <div className="p-6">Forbidden: admin/finance/developer only.</div>;
  }

  return (
    <div style={{ maxWidth: 960, margin: "24px auto", padding: 16 }}>
      <h1 className="text-2xl font-bold mb-4">Pending Users</h1>
      {loading && <div>Loading…</div>}
      {error && <div style={{ color: "crimson" }}>{error}</div>}

      {!loading && pending.length === 0 && <div style={{ marginTop: 12 }}>No pending users 🎉</div>}

      {!loading && pending.length > 0 && (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: 12,
            background: "white",
          }}
        >
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Company</th>
              <th style={thStyle}>Registered</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((u) => (
              <tr key={u.id}>
                <td style={tdStyle}>{u.full_name}</td>
                <td style={tdStyle}>{u.email}</td>
                <td style={tdStyle}>{u.company_name ? `${u.company_name} (#${u.company_id})` : u.company_id}</td>
                <td style={tdStyle}>{u.registered_at?.slice(0, 19).replace("T", " ")}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <button onClick={() => openApprove(u)} disabled={saving} style={{ marginRight: 8 }}>
                    Approve
                  </button>
                  <button onClick={() => reject(u.id, u.full_name)} disabled={saving} style={{ color: "crimson" }}>
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Approve modal */}
      {selectedUser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={() => !saving && setSelectedUser(null)}
        >
          <div
            style={{
              background: "white",
              padding: 20,
              minWidth: 500,
              maxHeight: "90vh",
              overflow: "auto",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-semibold text-lg mb-4">Approve {selectedUser.full_name}</h2>

            <label className="block mb-2">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{ padding: 8, width: "100%", border: "1px solid #ccc" }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>

            {/* ✅ Unit picker appears ONLY when backend says UNIT_REQUIRED */}
            {unitRequired && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Select Unit (required)</div>
                <select
                  value={unitId}
                  onChange={(e) => setUnitId(e.target.value)}
                  style={{ padding: 8, width: "100%", border: "1px solid #ccc" }}
                >
                  {units.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.unit_name} {u.slug ? `(${u.slug})` : ""} — #{u.id}
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 6, fontSize: 12, color: "#555" }}>
                  This company has multiple units. You must pick which unit becomes the user’s default.
                </div>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Assign Cost Centres</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 6,
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #eee",
                  padding: 8,
                }}
              >
                {centres.map((c) => (
                  <label key={c.cost_centre_id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedCC.has(c.cost_centre_id)}
                      onChange={() => toggleCC(c.cost_centre_id)}
                    />
                    <span>
                      {c.code || c.cost_centre_id} — {c.name || c.cost_centre_name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 20, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setSelectedUser(null)} disabled={saving}>
                Cancel
              </button>
              <button
                onClick={approve}
                disabled={saving || (unitRequired && !unitId)}
                style={{
                  padding: "8px 16px",
                  background: "#2563eb",
                  color: "white",
                  borderRadius: 4,
                  opacity: saving || (unitRequired && !unitId) ? 0.7 : 1,
                }}
              >
                {saving ? "Saving…" : "Approve"}
              </button>
            </div>

            {error && <div style={{ marginTop: 10, color: "crimson" }}>{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  padding: 8,
  background: "#f9fafb",
  fontWeight: 600,
};

const tdStyle = {
  padding: 8,
  borderBottom: "1px solid #eee",
};